import { frontendConfig } from '@kiku/config/env/frontend';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import { useDocumentTitle } from '~/hooks/use-document-title';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/mcp/')({
  component: McpPage
});

// In production VITE_API_BASE_URL is the full backend origin (e.g. https://api.getkiku.com).
// In local dev it's '/' (Vite proxy), so fall back to localhost.
const BACKEND_ORIGIN =
  frontendConfig.VITE_API_BASE_URL !== '/'
    ? frontendConfig.VITE_API_BASE_URL.replace(/\/$/, '')
    : 'http://localhost:3000';
const MCP_URL = `${BACKEND_ORIGIN}/mcp`;

const TOOLS = [
  { name: 'list_jobs', key: 'toolListJobs' },
  { name: 'get_job', key: 'toolGetJob' },
  { name: 'create_job', key: 'toolCreateJob' },
  { name: 'advance_job_setup_status', key: 'toolAdvanceSetup' },
  { name: 'get_candidate_profile', key: 'toolGetCandidate' },
  { name: 'list_applications', key: 'toolListApplications' }
] as const;

function CopyButton({ text }: { text: string }): React.ReactNode {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  return (
    <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
      {copied ? (
        <>
          <Check className="size-4" />
          {t('mcp.copied')}
        </>
      ) : (
        <>
          <Copy className="size-4" />
          {t('mcp.copyUrl')}
        </>
      )}
    </Button>
  );
}

function McpPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('mcp.title'));

  return (
    <PageContainer
      breadcrumbs={[{ label: t('mcp.title') }]}
      title={t('mcp.title')}
      subtitle={t('mcp.subtitle')}
    >
      <div className="flex flex-col gap-6">
        {/* What is MCP */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">{t('mcp.whatIsMcp')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('mcp.whatIsMcpDescription')}</p>
        </div>

        {/* Server URL */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">{t('mcp.serverUrl')}</h2>
          <div className="mt-3 flex items-center gap-3">
            <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm">{MCP_URL}</code>
            <CopyButton text={MCP_URL} />
          </div>
        </div>

        {/* Available Tools */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">{t('mcp.availableTools')}</h2>
          <div className="mt-3 divide-y divide-border">
            {TOOLS.map((tool) => (
              <div key={tool.name} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
                <code className="text-sm font-medium">{tool.name}</code>
                <p className="text-sm text-muted-foreground">{t(`mcp.${tool.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
